import React, { useState } from "react";
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  ClipboardDocumentIcon,
  ArchiveBoxIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import Alert from "../components/Alert";
import SecurityNotice from "../components/SecurityNotice";
import ActionButton from "../components/ActionButton";
import SEO from "../components/SEO";
import api from "../utils/api";
import { downloadFile } from "../utils/helpers";
import {
  generateStandardFilename,
  sanitizeFilename,
  generateTimestamp,
} from "../../shared/utils/fileHelpers";
import { downloadCSRBundle } from "../utils/zipHelper";
import { useScrollToResults } from "../hooks/useScrollToResults";
import { useCopyFeedback } from "../hooks/useCopyFeedback";
import countries from "../data/countries";

const organizationUnits = [
  "IT Department",
  "Administration",
  "Customer Service",
  "Engineering",
  "Finance",
  "Human Resources",
  "Legal",
  "Marketing",
  "Operations",
  "Product Development",
  "Research and Development",
  "Sales",
  "Security",
  "Support",
  "Technical Support",
  "Web Development",
  "Cloud Services",
  "Data Center",
  "DevOps",
  "E-Commerce",
  "Infrastructure",
  "Network Operations",
  "Quality Assurance",
  "Server Administration",
  "Software Development",
  "System Administration",
];

function CSRGenerator() {
  const [formData, setFormData] = useState({
    certificateType: "SSL/TLS",
    commonName: "",
    country: "US",
    state: "",
    city: "",
    organization: "",
    organizationUnit: "IT Department",
    email: "",
    keyType: "RSA",
    keySize: 2048,
    eccCurve: "P-256",
    digestAlgorithm: "SHA-256",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const { copyStatus, handleCopy, resetCopyStatus } = useCopyFeedback();
  const resultsRef = useScrollToResults(result);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.commonName) {
      newErrors.commonName = "A common name is required.";
    } else if (formData.certificateType === "SSL/TLS") {
      // Validate as domain for SSL/TLS Certificates
      const domainRegex = /^(\*\.)?([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
      if (!domainRegex.test(formData.commonName)) {
        newErrors.commonName = "This is not a valid domain name.";
      }
    } else if (formData.certificateType === "S/MIME") {
      // Validate as e-mail for S/MIME Certificates
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (!emailRegex.test(formData.commonName)) {
        newErrors.commonName = "This is not a valid e-mail address.";
      }
    }

    if (!formData.country) newErrors.country = "A country is required.";
    if (!formData.state) newErrors.state = "A state or province is required.";
    if (!formData.city) newErrors.city = "A city is required.";
    if (!formData.organization)
      newErrors.organization = "An organization is required.";
    if (!formData.email)
      newErrors.email = "An administrative e-mail address is required.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);
    setResult(null);
    // Stale copy feedback goes with the result it belonged to
    // (positional keys would otherwise light up the NEW result's row
    // inside the 2-second window).
    resetCopyStatus();

    try {
      const response = await api.generateCSR(formData);
      setResult(response.data);
    } catch (err) {
      setError(err.message || "The CSR could not be generated.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (content, type) => {
    const commonName = result?.commonName || formData.commonName;
    const fileType = type === "csr" ? "csr" : "privatekey";
    const filename = generateStandardFilename(commonName, fileType, "txt");
    downloadFile(content, filename);
  };

  const handleDownloadZip = async () => {
    const commonName = result?.commonName || formData.commonName;
    try {
      await downloadCSRBundle(
        result.csr,
        result.privateKey,
        commonName,
        generateStandardFilename,
        sanitizeFilename,
        generateTimestamp
      );
    } catch {
      setError("The ZIP archive could not be created.");
    }
  };

  const handleReset = () => {
    setFormData({
      certificateType: "SSL/TLS",
      commonName: "",
      country: "US",
      state: "",
      city: "",
      organization: "",
      organizationUnit: "IT Department",
      email: "",
      keyType: "RSA",
      keySize: 2048,
      eccCurve: "P-256",
      digestAlgorithm: "SHA-256",
    });
    setErrors({});
    setResult(null);
    setError(null);
    resetCopyStatus();
  };

  const fillExample = () => {
    if (formData.certificateType === "S/MIME") {
      setFormData((prev) => ({
        ...prev,
        commonName: "john.doe@example.com",
        country: "US",
        state: "California",
        city: "San Francisco",
        organization: "Example Company Inc.",
        organizationUnit: "IT Department",
        email: "admin@example.com",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        commonName: "www.example.com",
        country: "US",
        state: "California",
        city: "San Francisco",
        organization: "Example Company Inc.",
        organizationUnit: "IT Department",
        email: "admin@example.com",
      }));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 2026-07-04 tools-wide overhaul (operator-approved) : added the
          canonicalUrl so this page matches the site-wide SEO contract
          (see src/client/components/SEO.jsx and the /csr entry in
          build/generate-static-pages.js). "private key" is lowercase in
          the description : descriptive nouns are lowercase in
          SENTENCES, meta text included, one way everywhere (operator
          ruling 2026-08-28). */}
      <SEO
        title="CSR Generator - Create Certificate Signing Request"
        description="Generate a Certificate Signing Request (CSR) and private key online. Create CSR for SSL/TLS or S/MIME Certificates with RSA or ECC. Free CSR tool with SHA-256, SHA-384, and SHA-512 support."
        keywords="CSR generator, Certificate Signing Request, SSL CSR, S/MIME CSR, create CSR online, RSA CSR, ECC CSR, generate private key, Certificate Request, CSR tool, SHA-256 CSR, SHA-512 CSR"
        canonicalUrl="https://tools.trustico.com/csr"
      />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-heading mb-4 text-center">
          CSR & Private Key Generator
        </h1>
        <p className="text-body">
          Create a Certificate Signing Request (CSR) and a matching private key
          for your SSL Certificate or S/MIME Certificate. The CSR carries your
          public key and your identifying details, and it is what a Certificate
          Authority (CA) signs when issuing an SSL Certificate. Choose RSA or
          Elliptic Curve Cryptography (ECC) keys and your preferred signing
          algorithm, then copy or download both files. Keep the private key
          safe, because an SSL Certificate cannot be installed without it.
        </p>
      </div>

      <SecurityNotice />

      <form onSubmit={handleSubmit} className="card p-6">
        <h2 className="text-lg font-semibold text-heading mb-2">
          Certificate Request Information
        </h2>
        <p className="text-body mb-4">
          Fill in the information below to generate a Certificate Signing
          Request (CSR) and private key. Fields marked with an asterisk (*) are
          required.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Certificate Type */}
          <div className="md:col-span-2">
            <label htmlFor="certificateType" className="label">
              Certificate Type
            </label>
            <select
              id="certificateType"
              name="certificateType"
              value={formData.certificateType}
              onChange={handleChange}
              className="input"
            >
              <option value="SSL/TLS">SSL/TLS Certificate</option>
              <option value="S/MIME">S/MIME Certificate (E-Mail)</option>
            </select>
          </div>

          {/* Common Name */}
          <div className="md:col-span-2">
            <label htmlFor="commonName" className="label">
              Common Name *
            </label>
            <input
              type="text"
              id="commonName"
              name="commonName"
              value={formData.commonName}
              onChange={handleChange}
              placeholder={
                formData.certificateType === "S/MIME"
                  ? "user@example.com"
                  : "www.example.com or *.example.com"
              }
              className={`input ${errors.commonName ? "border-red-500" : ""}`}
            />
            {errors.commonName && (
              <p className="error-text">{errors.commonName}</p>
            )}
          </div>

          {/* Organization */}
          <div>
            <label htmlFor="organization" className="label">
              Organization *
            </label>
            <input
              type="text"
              id="organization"
              name="organization"
              value={formData.organization}
              onChange={handleChange}
              placeholder="Acme Inc."
              className={`input ${errors.organization ? "border-red-500" : ""}`}
            />
            {errors.organization && (
              <p className="error-text">{errors.organization}</p>
            )}
          </div>

          {/* Organization Unit */}
          <div>
            <label htmlFor="organizationUnit" className="label">
              Organization Unit
            </label>
            <select
              id="organizationUnit"
              name="organizationUnit"
              value={formData.organizationUnit}
              onChange={handleChange}
              className="input"
            >
              {organizationUnits.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>

          {/* City */}
          <div>
            <label htmlFor="city" className="label">
              City *
            </label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="San Francisco"
              className={`input ${errors.city ? "border-red-500" : ""}`}
            />
            {errors.city && <p className="error-text">{errors.city}</p>}
          </div>

          {/* State */}
          <div>
            <label htmlFor="state" className="label">
              State/Province *
            </label>
            <input
              type="text"
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              placeholder="California"
              className={`input ${errors.state ? "border-red-500" : ""}`}
            />
            {errors.state && <p className="error-text">{errors.state}</p>}
          </div>

          {/* Country */}
          <div>
            <label htmlFor="country" className="label">
              Country *
            </label>
            <select
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={`input ${errors.country ? "border-red-500" : ""}`}
            >
              {countries.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
            {errors.country && <p className="error-text">{errors.country}</p>}
          </div>

          {/* Administrative E-Mail */}
          <div>
            <label htmlFor="email" className="label">
              Administrative E-Mail *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@example.com"
              className={`input ${errors.email ? "border-red-500" : ""}`}
            />
            {errors.email && <p className="error-text">{errors.email}</p>}
          </div>
        </div>

        {/* Key Configuration Section.
            2026-07-04 operator consistency directive : "text-md" is
            not a Tailwind class (the scale goes text-sm -> text-base
            -> text-lg), so this sub-heading silently rendered at the
            inherited size. "text-base font-semibold" is the standard
            SUB-heading tier (a subsection inside a section card) ;
            section headings themselves are text-lg font-semibold
            per the CSRGenerator reference hierarchy.
            Between two field grids rather than inside one (operator
            ruling 2026-10-01) : inside a grid, any margin this block
            carries adds to the grid's own 24px gap, so its paragraph can
            never sit the 16px every paragraph keeps to what it
            introduces. Here it takes the 32px gap between groups above
            and exactly 16px down to the key fields. */}
        <div className="mt-8">
          <h3 className="text-base font-semibold text-heading mb-2">
            Key Configuration
          </h3>
          <p className="text-body mb-4">
            Choose the cryptographic algorithm and key strength for your SSL
            Certificate. RSA 2048 bits is the most widely supported option,
            while ECC offers equivalent security with smaller key sizes.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Key Type */}
          <div>
            <label htmlFor="keyType" className="label">
              Key Type
            </label>
            <select
              id="keyType"
              name="keyType"
              value={formData.keyType}
              onChange={handleChange}
              className="input"
            >
              <option value="RSA">RSA</option>
              <option value="ECC">ECC (Elliptic Curve)</option>
            </select>
          </div>

          {/* Key Size / Curve */}
          <div>
            {formData.keyType === "RSA" ? (
              <>
                <label htmlFor="keySize" className="label">
                  Key Size
                </label>
                <select
                  id="keySize"
                  name="keySize"
                  value={formData.keySize}
                  onChange={handleChange}
                  className="input"
                >
                  <option value={2048}>2048 bits (Recommended)</option>
                  <option value={3072}>3072 bits</option>
                  <option value={4096}>4096 bits</option>
                </select>
              </>
            ) : (
              <>
                <label htmlFor="eccCurve" className="label">
                  ECC Curve
                </label>
                <select
                  id="eccCurve"
                  name="eccCurve"
                  value={formData.eccCurve}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="P-256">P-256</option>
                  <option value="P-384">P-384</option>
                  <option value="P-521">P-521</option>
                </select>
              </>
            )}
          </div>

          {/* Digest Algorithm */}
          <div>
            <label htmlFor="digestAlgorithm" className="label">
              Digest Algorithm
            </label>
            <select
              id="digestAlgorithm"
              name="digestAlgorithm"
              value={formData.digestAlgorithm}
              onChange={handleChange}
              className="input"
            >
              <option value="SHA-256">SHA-256 (Recommended)</option>
              <option value="SHA-384">SHA-384</option>
              <option value="SHA-512">SHA-512</option>
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          {/* Three-across only from lg : ActionButton's sm min-width
              is 240px, and three of those plus gaps (752px) exceed
              what a 640-800px viewport offers, overlapping the
              equal-size buttons (the same defect class the operator
              reported on /chain, 2026-08-07). At sm the third button
              wraps to a second row at the same cell size. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-4xl">
            {/* 2026-07-04 operator consistency directive : the primary
                action is disabled until every required text field is
                non-empty (Country defaults to US and the selects always
                hold a value, so only the free-text fields gate).
                validateForm keeps its required-field checks as the
                backstop for direct form submission (the Enter-key path,
                mirroring the handleSubmit guards on ChainAnalyzer and
                CertificateMatcher) and still supplies the on-click
                FORMAT validation for the Common Name. */}
            <ActionButton
              type="submit"
              variant="primary"
              disabled={
                loading ||
                !formData.commonName.trim() ||
                !formData.organization.trim() ||
                !formData.city.trim() ||
                !formData.state.trim() ||
                !formData.email.trim()
              }
              loading={loading}
              loadingText="Generating..."
              icon={DocumentTextIcon}
            >
              Generate CSR
            </ActionButton>
            <ActionButton
              type="button"
              onClick={handleReset}
              variant="outline"
              icon={XMarkIcon}
            >
              {/* 2026-07-04 operator consistency directive : reset
                  buttons across every tool page say "Reset" (the
                  previous label was "Clear Data"). */}
              Reset
            </ActionButton>
            <ActionButton
              type="button"
              onClick={fillExample}
              variant="outline"
              icon={ClipboardDocumentIcon}
            >
              Fill Example Data
            </ActionButton>
          </div>
        </div>
      </form>

      {error && (
        <Alert type="error" className="mt-6" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {result && (
        <div ref={resultsRef} className="mt-8 space-y-6 animate-fade-in">
          {/* CSR */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Certificate Signing Request (CSR)
            </h2>
            <p className="text-body mb-4">
              Submit this CSR to your Certificate Authority (CA) to obtain your
              SSL Certificate. The CSR contains your public key and the
              information you provided above. Keep this CSR safe as you may need
              it for reissuing, replacing or purchasing a new license.
            </p>
            <textarea
              value={result.csr}
              readOnly
              className="textarea certificate-textarea mb-4"
              rows={12}
            />
            <div className="flex justify-center">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                <ActionButton
                  onClick={() => handleCopy(result.csr, "csr")}
                  variant="outline"
                  icon={ClipboardDocumentIcon}
                >
                  {copyStatus.csr ? "Copied!" : "Copy CSR"}
                </ActionButton>
                <ActionButton
                  onClick={() => handleDownload(result.csr, "csr")}
                  variant="outline"
                  icon={ArrowDownTrayIcon}
                >
                  Download CSR
                </ActionButton>
              </div>
            </div>
          </div>

          {/* Private Key */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-heading mb-2">
              Private Key
            </h2>
            {/* 2026-07-04 operator consistency directive : every
                section heading carries an explanatory paragraph ; this
                card was the only result card on the page without one. */}
            <p className="text-body mb-4">
              Store this private key securely and never share it. You will need
              it to install your SSL Certificate on your server, and Trustico®
              does not retain a copy.
            </p>
            <textarea
              value={result.privateKey}
              readOnly
              className="textarea certificate-textarea mb-4"
              rows={12}
            />
            <div className="flex justify-center">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
                <ActionButton
                  onClick={() => handleCopy(result.privateKey, "key")}
                  variant="outline"
                  icon={ClipboardDocumentIcon}
                >
                  {copyStatus.key ? "Copied!" : "Copy Private Key"}
                </ActionButton>
                <ActionButton
                  onClick={() => handleDownload(result.privateKey, "key")}
                  variant="outline"
                  icon={ArrowDownTrayIcon}
                >
                  Download Private Key
                </ActionButton>
              </div>
            </div>
          </div>

          {/* The archive is a BUTTON, not a section (operator, 2026-08-23 :
              "it is simply a button that does not require a blue box and
              text"). It had a tinted card, a heading and a paragraph of
              its own, which no sibling tool gives its archive : the Chain
              Analyzer and the Issuance Tool both put "Download ZIP
              Archive" in the action row beside the other downloads. The
              label matches them for the same reason - one name for one
              thing across the site. */}
          <div className="flex justify-center">
            <ActionButton
              onClick={handleDownloadZip}
              variant="primary"
              icon={ArchiveBoxIcon}
            >
              Download ZIP Archive
            </ActionButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default CSRGenerator;
